import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { resetPassword } from '../core/_requests'
import { LanguageHelp } from '../../../../_metronic/helpers/LanguageHelper'
import { PasswordMeterComponent } from '../../../../_metronic/assets/ts/components'
import { useNavigate } from 'react-router-dom';

const initialValues = {
  Password: '',
  changepassword: '',
}

const forgotPasswordSchema = Yup.object().shape({
  Password: Yup.string()
    .min(8, LanguageHelp('Must contain 8 characters!'))
    .max(25, LanguageHelp('Must contain no more than 25 characters!'))
    .matches(/^(?=.*[\!\@\#\$\%\^\'\&\*\_\+\=\|\;\:\,\.\~\-])/, LanguageHelp("One special character required! -> !@#$%^'&*-_+=|;:,.~"))
    .matches(/^(?=.*[0-9])/, LanguageHelp("One special character required!"))
    .matches(/^(?=.*[a-z])/, LanguageHelp("One lowercase required!"))
    .matches(/^(?=.*[A-Z])/, LanguageHelp("One uppercase required!"))
    .required(LanguageHelp('Password is required')),
  changepassword: Yup.string()
    .required(LanguageHelp('Password confirmation is required'))
    .when('Password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('Password')], LanguageHelp("The passwords you entered do not match.")),
    }),
})

export function ResetPassword() {
  const [loading, setLoading] = useState(false)
  const [isPasswordShow, setIsPasswordShow] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const queryParameters = new URLSearchParams(window.location.search)
  const c = queryParameters.get("c")
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)
      const { data: result } = await resetPassword(c as string, values.Password, values.changepassword)
      if (result.httpStatusCode === 200) {
        setHasErrors(false)
        setLoading(false)
        setTimeout(function () {
          navigate('/auth')
        }, 1000);
      }
      else {
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        setStatus('The login detail is incorrect')
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>{LanguageHelp("Reset Password")}</h1>
        {/* end::Title */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {LanguageHelp("Sorry, looks like there are some errors detected, please try again.")}
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-success p-8 rounded'>
          <div className='text'>{LanguageHelp("Your password has been changed successfully. Please log in to continue.")}</div>
        </div>
      )}
      {/* end::Title */}

      {/* begin::Form group Password */}
      <div className='fv-row mb-8' data-kt-password-meter='true'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Password")}</label>
          <div className='position-relative mb-3'>
            <div className="input-group mb-5">
              <input
                type={isPasswordShow ? 'text' : 'password'}
                placeholder={LanguageHelp("Password")}
                autoComplete='off'
                {...formik.getFieldProps('Password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.Password && formik.errors.Password,
                  },
                  {
                    'is-valid': formik.touched.Password && !formik.errors.Password,
                  }
                )}
              />
              <span className="input-group-text" onClick={(e) => { setIsPasswordShow(!isPasswordShow); }}>
                {isPasswordShow && <i style={{ cursor: "pointer", minWidth: "30px" }} className='far fa-eye-slash fs-2 text-primary'></i>}
                {!isPasswordShow && <i style={{ cursor: "pointer", minWidth: "30px" }} className='far fa-eye fs-2 text-primary'></i>}
              </span>
            </div>

            {formik.touched.Password && formik.errors.Password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.Password}</span>
                </div>
              </div>
            )}
          </div>
          {/* begin::Meter */}
          <div
            className='d-flex align-items-center mb-3'
            data-kt-password-meter-control='highlight'
          >
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
          </div>
          {/* end::Meter */}
        </div>

        {formik.touched.Password && formik.errors.Password && (
          <div className='form-text'>
            {LanguageHelp('Your password should be 8-25 characters long and should contain at least one lowercase, one uppercase, one numeric digit and one special characters! !@#$%^\'&*-_+=|;:,.~')}
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm password */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Confirm Password")}</label>
        <div className="input-group mb-5">
          <input
            type={isPasswordShow ? 'text' : 'password'}
            placeholder={LanguageHelp("Confirm Password")}
            autoComplete='off'
            {...formik.getFieldProps('changepassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
              },
              {
                'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
              }
            )}
          />
          <span className="input-group-text" onClick={(e) => { setIsPasswordShow(!isPasswordShow); }}>
            {isPasswordShow && <i style={{ cursor: "pointer", minWidth: "30px" }} className='far fa-eye-slash fs-2 text-primary'></i>}
            {!isPasswordShow && <i style={{ cursor: "pointer", minWidth: "30px" }} className='far fa-eye fs-2 text-primary'></i>}
          </span>
        </div>

        {formik.touched.changepassword && formik.errors.changepassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.changepassword}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
          <span className='indicator-label'>{LanguageHelp("Submit")}</span>
          {loading && (
            <span className='indicator-progress'>
              {LanguageHelp("Please wait...")}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {LanguageHelp("Cancel")}
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
